/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, createContext, forwardRef } from 'react';
import { type ContextValue, ProgressBar as ProgressBarCore, type ProgressBarProps as ProgressBarCoreProps, useContextProps } from 'react-aria-components';
import { $ } from '../../tokens.stylex';

interface ProgressIndicatorProps extends Omit<ProgressBarCoreProps, 'style' | 'className' | 'children' | 'isIndeterminate' | 'value'> {
  /** The size variant of the progress indicator. (Default: 16) */
  size?: keyof typeof sizeVariants;
  /** The variant of the progress indicator. (Default: primary) */
  variant?: keyof typeof indicatorVariantVariants;
  style?: stylex.StyleXStyles;
}

const ProgressIndicatorContext = createContext<ContextValue<ProgressIndicatorProps, HTMLDivElement>>({});

const ProgressIndicator = forwardRef((props: ProgressIndicatorProps, ref: ForwardedRef<HTMLDivElement>) => {
  [props, ref] = useContextProps(props, ref, ProgressIndicatorContext);

  const { style, size = 16, variant = 'primary', ...coreProps } = props;

  const center = size / 2;
  const radius = center - STROKE_WIDTH / 2;
  const circumference = 2 * radius * Math.PI;

  return (
    <ProgressBarCore
      ref={ref}
      aria-label="Loading..."
      isIndeterminate
      {...stylex.props(styles.base, sizeVariants[size], style)}
      {...coreProps}>
      <svg viewBox={`0 0 ${size} ${size}`} {...stylex.props(spinnerStyles.base)}>
        <circle
          cx={center}
          cy={center}
          r={radius}
          {...stylex.props(trackStyles.base, trackVariantVariants[variant])}
        />
        <circle
          cx={center}
          cy={center}
          r={radius}
          {...stylex.props(indicatorStyles.base({ center, circumference }), indicatorVariantVariants[variant])}
        />
      </svg>
    </ProgressBarCore>
  );
});

const STROKE_WIDTH = 2;

const styles = stylex.create({
  base: {
    alignItems: 'center',
    display: 'inline-flex',
    justifyContent: 'center',
    position: 'relative',
  },
});

const sizeVariants = stylex.create({
  8: {
    height: 8,
    width: 8,
  },
  12: {
    height: 12,
    width: 12,
  },
  16: {
    height: 16,
    width: 16,
  },
  20: {
    height: 20,
    width: 20,
  },
  24: {
    height: 24,
    width: 24,
  },
  32: {
    height: 32,
    width: 32,
  },
  48: {
    height: 48,
    width: 48,
  },
});

const spin = stylex.keyframes({
  from: {
    transform: 'rotate(0deg)',
  },
  to: {
    transform: 'rotate(360deg)',
  },
});

const spinnerStyles = stylex.create({
  base: {
    animationDuration: '0.5s',
    animationIterationCount: 'infinite',
    animationName: spin,
    animationTimingFunction: 'linear',
    fill: 'none',
    height: '100%',
    strokeWidth: STROKE_WIDTH,
    width: '100%',
  }
});

const trackStyles = stylex.create({
  base: {
    fill: 'none',
    stroke: $.border08,
  },
});

const trackVariantVariants = stylex.create({
  primary: {
    stroke: $.border08,
  },
  interactive: {
    stroke: $.borderInteractiveSilent,
  },
  inverted: {
    stroke: $.opacity12Inverted,
  },
});

const indicatorStyles = stylex.create({
  base: (props: { center: number, circumference: number }) => ({
    fill: 'none',
    strokeDasharray: `${props.circumference} ${props.circumference}`,
    strokeDashoffset: props.circumference * 0.8,
    strokeLinecap: 'round',
    transform: `rotate(-90 ${props.center} ${props.center})`,
    transition: 'stroke-dashoffset 0.3s ease',
  })
});

const indicatorVariantVariants = stylex.create({
  primary: {
    stroke: $.iconPrimary,
  },
  interactive: {
    stroke: $.iconInteractive,
  },
  inverted: {
    stroke: $.iconInverted,
  },
});

const PROGRESS_INDICATOR_MIN_DELAY_DURATION = 300;

export { PROGRESS_INDICATOR_MIN_DELAY_DURATION, ProgressIndicator, ProgressIndicatorContext, type ProgressIndicatorProps };
